import React from 'react';
import { motion } from 'framer-motion';
import {
    Star, TrendingUp, TrendingDown, Minus, Mail, MessageSquare, Clock
} from 'lucide-react';

const PersonCard = ({ person, darkMode, onClick, delay = 0 }) => {
    const getTrendIcon = () => {
        switch (person.trend) {
            case 'up': return <TrendingUp size={12} className="text-emerald-500" />;
            case 'down': return <TrendingDown size={12} className="text-red-500" />;
            default: return <Minus size={12} className="text-slate-400" />;
        }
    };

    const getScoreColor = () => {
        if (person.score >= 80) return 'text-emerald-500 bg-emerald-500/10';
        if (person.score >= 50) return 'text-yellow-500 bg-yellow-500/10';
        return 'text-red-500 bg-red-500/10';
    };

    const getSentimentEmoji = () => {
        switch (person.sentiment) {
            case 'positive': return '😊';
            case 'negative': return '😟';
            default: return '😐';
        }
    };

    const getTimeAgo = (date) => {
        if (!date) return 'Never';
        const now = new Date();
        const then = new Date(date);
        const diff = now - then;
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return `${Math.floor(days / 7)}w ago`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            whileHover={{ scale: 1.02 }}
            onClick={onClick}
            className={`relative p-4 rounded-2xl border cursor-pointer transition-all ${darkMode
                    ? 'bg-white/5 border-white/10 hover:border-white/20'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg'
                }`}
        >
            {/* VIP Badge */}
            {person.isVip && (
                <div className="absolute top-3 right-3">
                    <Star size={16} className="text-yellow-500" fill="currentColor" />
                </div>
            )}

            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
                <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-purple-500 flex items-center justify-center text-white text-lg font-medium">
                        {person.avatar ? (
                            <img src={person.avatar} alt={person.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                            person.name?.charAt(0) || '?'
                        )}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 text-lg`}>
                        {getSentimentEmoji()}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={`font-medium truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {person.name}
                    </h3>
                    <p className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {person.email}
                    </p>
                    {person.company && (
                        <p className={`text-xs truncate ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {person.company}
                        </p>
                    )}
                </div>
            </div>

            {/* Score */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${getScoreColor().split(' ')[0]}`}>
                        {person.score}
                    </span>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${getScoreColor()}`}>
                        {getTrendIcon()}
                    </div>
                </div>
                <div className={`flex items-center gap-1 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Clock size={12} />
                    {getTimeAgo(person.lastInteraction)}
                </div>
            </div>

            {/* Platform Stats */}
            <div className="flex items-center gap-3">
                {person.platforms?.email && (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                        <Mail size={12} className="text-red-500" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {person.platforms.email.count}
                        </span>
                    </div>
                )}
                {person.platforms?.slack?.count > 0 && (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                        <MessageSquare size={12} className="text-purple-500" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {person.platforms.slack.count}
                        </span>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default PersonCard;
